<template>
  <v-card class="table-card" elevation="0">
    <v-data-table
      :items="reportRows"
      :headers="headers"
      hide-default-footer
      class="report-table bordered"
    >
      <template v-slot:item.agent_display="{ item }">
        {{ formatAgent(item.agent_display) }}
      </template>
      <template v-slot:item.answered="{ item }">
        <v-row v-if="!isMobile" no-gutters>
          <v-col cols="6">{{ item.answered }}</v-col>
          <v-col cols="6" class="text--secondary">{{ percentageTemplate(item.answered / item.offered) }}</v-col>
        </v-row>
        <span v-else>
          {{ item.answered }}&nbsp;
          <span class="text--secondary">{{ percentageTemplate(item.answered / item.offered) }}</span>
        </span>
      </template>
      <template v-slot:item.min_talk="{ item }">
        {{ hourTemplate(item.min_talk) }}
      </template>
      <template v-slot:item.avg_talk="{ item }">
        {{ hourTemplate(item.avg_talk) }}
      </template>
      <template v-slot:item.max_talk="{ item }">
        {{ hourTemplate(item.max_talk) }}
      </template>
      <template v-slot:item.total_talk="{ item }">
        {{ hourTemplate(item.total_talk) }}
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn icon color="primary" @click="loadAgentDetails(item.agent, formatAgent(item.agent_display))">
          <v-icon>mdi-information-outline</v-icon>
        </v-btn>
      </template>
      <template slot="body.append">
        <tr v-if="isMobile" class="v-data-table__mobile-table-row">
          <td class="v-data-table__mobile-row">
            <div class="v-data-table__mobile-row__header">{{ $t('queues.reports.agent.summary.total') }}</div>
            <div class="v-data-table__mobile-row__cell"></div>
          </td>
          <td class="v-data-table__mobile-row">
            <div class="v-data-table__mobile-row__header">{{ $t('queues.reports.agent.summary.offered') }}</div>
            <div class="v-data-table__mobile-row__cell">{{ totals.offered }}</div>
          </td>
          <td class="v-data-table__mobile-row">
            <div class="v-data-table__mobile-row__header">{{ $t('queues.reports.agent.summary.answered') }}</div>
            <div class="v-data-table__mobile-row__cell">
              {{ totals.answered }}&nbsp;
              <span class="text--secondary">{{ percentageTemplate(totals.answered / totals.offered) }}</span>
            </div>
          </td>
          <td class="v-data-table__mobile-row">
            <div class="v-data-table__mobile-row__header">{{ $t('queues.reports.agent.summary.minTime') }}</div>
            <div class="v-data-table__mobile-row__cell">{{ hourTemplate(totals.min_talk) }}</div>
          </td>
          <td class="v-data-table__mobile-row">
            <div class="v-data-table__mobile-row__header">{{ $t('queues.reports.agent.summary.avgTime') }}</div>
            <div class="v-data-table__mobile-row__cell">{{ hourTemplate(totals.avg_talk) }}</div>
          </td>
          <td class="v-data-table__mobile-row">
            <div class="v-data-table__mobile-row__header">{{ $t('queues.reports.agent.summary.maxTime') }}</div>
            <div class="v-data-table__mobile-row__cell">{{ hourTemplate(totals.max_talk) }}</div>
          </td>
          <td class="v-data-table__mobile-row">
            <div class="v-data-table__mobile-row__header">{{ $t('queues.reports.agent.summary.totalTime') }}</div>
            <div class="v-data-table__mobile-row__cell">{{ hourTemplate(totals.total_talk) }}</div>
          </td>
        </tr>
        <tr v-else class="font-weight-bold totals">
          <td>{{ $t('queues.reports.agent.summary.total') }}</td>
          <td class="text-center">{{ totals.offered }}</td>
          <td class="text-center bordered answered">
            <v-row no-gutters>
              <v-col cols="6">{{ totals.answered }}</v-col>
              <v-col cols="6" class="text--secondary">{{ percentageTemplate(totals.answered / totals.offered) }}</v-col>
            </v-row>
          </td>
          <td class="text-center">{{ hourTemplate(totals.min_talk) }}</td>
          <td class="text-center">{{ hourTemplate(totals.avg_talk) }}</td>
          <td class="text-center">{{ hourTemplate(totals.max_talk) }}</td>
          <td class="text-center">{{ hourTemplate(totals.total_talk) }}</td>
          <td></td>
        </tr>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import formatTime from '@/filters/format_time'
import formatClid from '@/filters/format_clid'
import { formatPercentage } from '@/filters/format_number'

export default {
  name: 'queues-reports-agent-summary',
  props: {
    reportRows: {
      type: Array,
      required: true
    },
    totals: {
      type: Object,
      required: true
    },
    startDate: {
      type: String
    },
    endDate: {
      type: String
    },
    scope: {
      type: String,
      default: 'month'
    }
  },
  computed: {
    isMobile () {
      return this.$vuetify.breakpoint.mobile
    },
    headers () {
      return [
        { text: this.$t('queues.reports.agent.summary.agent'), value: 'agent_display' },
        { text: this.$t('queues.reports.agent.summary.offered'), value: 'offered', align: 'center' },
        { text: this.$t('queues.reports.agent.summary.answered'), value: 'answered', align: 'center', class: 'blue--text text--lighten-1 bordered answered', cellClass: 'bordered answered' },
        { text: this.$t('queues.reports.agent.summary.minTime'), value: 'min_talk', align: 'center' },
        { text: this.$t('queues.reports.agent.summary.avgTime'), value: 'avg_talk', align: 'center' },
        { text: this.$t('queues.reports.agent.summary.maxTime'), value: 'max_talk', align: 'center' },
        { text: this.$t('queues.reports.agent.summary.totalTime'), value: 'total_talk', align: 'center' },
        { text: '', value: 'actions', sortable: false, align: 'end' }
      ]
    }
  },
  methods: {
    formatAgent (agent) {
      if (agent === 'NONE') return this.$t('queues.reports.none')
      return formatClid(agent)
    },
    hourTemplate (seconds) {
      if (!seconds) return '00:00:00'
      return formatTime(seconds, true)
    },
    percentageTemplate (value) {
      return formatPercentage(value, 0)
    },
    loadAgentDetails (agent, display) {
      const extension = agent.split('@')[0]
      this.$emit('refreshData', {
        reportType: 'agentDetails',
        startDate: this.startDate,
        endDate: this.endDate,
        agentExtension: extension,
        agentDisplay: display,
        scope: this.scope,
        breadcrumb_op: 'add'
      })
    }
  }
}
</script>
